// @spec INC-STR-008
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { Plus, Tag } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DeleteTagDialog,
  TagFormDialog,
  TagsList,
  useCreateIncidentTag,
  useDeleteIncidentTag,
  useIncidentTags,
  useUpdateIncidentTag,
} from '@/features/incidents';
import type { CreateTagInput, IncidentTag, UpdateTagInput } from '@/features/incidents';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/admin/incidents/tags')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.incidentTags'),
  }),
  component: IncidentTagsPage,
});

/**
 * Admin page for managing incident tags.
 *
 * Allows organization admins to:
 * - View all incident tags with usage counts
 * - Create new tags with name, description, and color
 * - Edit existing tag properties
 * - Activate/deactivate tags
 * - Delete unused tags
 */
function IncidentTagsPage() {
  const { t } = useTranslation('admin');
  const { isAdmin } = useOrgRole();

  // State for dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<IncidentTag | null>(null);
  const [deletingTag, setDeletingTag] = useState<IncidentTag | null>(null);

  // API hooks
  const { data: tagsData, isLoading: isLoadingTags } = useIncidentTags({
    includeInactive: true,
  });

  const createMutation = useCreateIncidentTag();
  const updateMutation = useUpdateIncidentTag();
  const deleteMutation = useDeleteIncidentTag();

  const tags = tagsData?.items ?? [];
  const activeTags = tags.filter((t) => t.isActive);
  const inactiveTags = tags.filter((t) => !t.isActive);

  // Handlers
  const handleCreateOpen = () => {
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (tag: IncidentTag) => {
    setEditingTag(tag);
  };

  const handleDelete = (tag: IncidentTag) => {
    setDeletingTag(tag);
  };

  const handleToggleActive = async (tag: IncidentTag) => {
    try {
      await updateMutation.mutateAsync({
        tagId: tag.id,
        input: { isActive: !tag.isActive },
      });
      toast.success(
        tag.isActive
          ? `Tag "${tag.name}" has been deactivated`
          : `Tag "${tag.name}" has been activated`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update tag';
      toast.error(message);
    }
  };

  const handleCreateSubmit = async (data: CreateTagInput | UpdateTagInput, isUpdate: boolean) => {
    if (isUpdate) {
      // This shouldn't happen from create dialog
      return;
    }

    try {
      const result = await createMutation.mutateAsync(data as CreateTagInput);
      toast.success(t('admin:toast.tagCreatedSuccessfully', { result: result.name }));
      setIsCreateDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create tag';
      toast.error(message);
      throw error;
    }
  };

  const handleUpdateSubmit = async (data: CreateTagInput | UpdateTagInput, isUpdate: boolean) => {
    if (!editingTag || !isUpdate) return;

    try {
      await updateMutation.mutateAsync({
        tagId: editingTag.id,
        input: data as UpdateTagInput,
      });
      toast.success(t('admin:toast.tagUpdatedSuccessfully', { editingTag: editingTag.name }));
      setEditingTag(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update tag';
      toast.error(message);
      throw error;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTag) return;

    try {
      await deleteMutation.mutateAsync(deletingTag.id);
      toast.success(t('admin:toast.tagDeletedSuccessfully', { deletingTag: deletingTag.name }));
      setDeletingTag(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete tag';
      toast.error(message);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <h2 className="text-lg font-medium text-destructive">{t('common:accessDenied.title')}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('common:accessDenied.incidentTagsDenied')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        <header className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-900">
              <Tag className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Incident Tags</h1>
              <p className="mt-1 text-muted-foreground">
                Configure tags for multi-dimensional incident classification.
              </p>
            </div>
          </div>

          <Button onClick={handleCreateOpen}>
            <Plus className="mr-2 h-4 w-4" />
            Add Tag
          </Button>
        </header>

        {/* Stats summary */}
        {!isLoadingTags && tags.length > 0 && (
          <div className="flex gap-4 text-sm text-muted-foreground">
            <span>
              {tags.length} {tags.length === 1 ? 'tag' : 'tags'} total
            </span>
            <span>{activeTags.length} active</span>
            {inactiveTags.length > 0 && (
              <span className="text-amber-600">{inactiveTags.length} inactive</span>
            )}
          </div>
        )}

        {/* Tags list */}
        <TagsList
          tags={tags}
          isLoading={isLoadingTags}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
        />

        {/* Create Dialog */}
        <TagFormDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onSubmit={handleCreateSubmit}
          isSubmitting={createMutation.isPending}
        />

        {/* Edit Dialog */}
        {editingTag && (
          <TagFormDialog
            open={!!editingTag}
            onOpenChange={(open) => {
              if (!open) setEditingTag(null);
            }}
            tag={editingTag}
            onSubmit={handleUpdateSubmit}
            isSubmitting={updateMutation.isPending}
          />
        )}

        {/* Delete Dialog */}
        <DeleteTagDialog
          open={!!deletingTag}
          onOpenChange={(open) => {
            if (!open) setDeletingTag(null);
          }}
          tag={deletingTag}
          onConfirm={handleDeleteConfirm}
          isDeleting={deleteMutation.isPending}
        />
      </div>
    </div>
  );
}
